import { ClientFragment } from "@graphql/clients/clients.g";
import React from "react";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";

import {
  CLIENT_TABLE_COLUMN_LABELS,
  ClientTableColumns,
  DEFAULT_CLIENT_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_TABLE_COLUMN_SETTINGS,
} from "./UI/ClientsTableColumns";

/**
 * Default filter values for Clients
 */
export const DEFAULT_CLIENT_FILTER_VALUES = {
  nameSearch: "",
  domainSearch: "",
  emailSearch: "",
};

/**
 * Comprehensive table configuration for Clients
 */
export const ClientsTableConfig = createTableConfiguration<
  ClientFragment,
  ClientTableColumns
>({
  tableId: "clients",
  columns: {
    [ClientTableColumns.name]: {
      id: ClientTableColumns.name,
      label: CLIENT_TABLE_COLUMN_LABELS[ClientTableColumns.name],
      fieldPath: "name",
      sortField: "name",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: ClientFragment) => {
        return renderName(value, row, () => {});
      },
    },
    [ClientTableColumns.domain]: {
      id: ClientTableColumns.domain,
      label: CLIENT_TABLE_COLUMN_LABELS[ClientTableColumns.domain],
      fieldPath: "domain",
      sortField: "domain",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderDomain(value);
      },
    },
    [ClientTableColumns.email]: {
      id: ClientTableColumns.email,
      label: CLIENT_TABLE_COLUMN_LABELS[ClientTableColumns.email],
      fieldPath: "email",
      sortField: "email",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderEmail(value);
      },
    },
    [ClientTableColumns.createdAt]: {
      id: ClientTableColumns.createdAt,
      label: CLIENT_TABLE_COLUMN_LABELS[ClientTableColumns.createdAt],
      fieldPath: "createdAt",
      sortField: "created_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderCreatedAt(value);
      },
    },
    [ClientTableColumns.updatedAt]: {
      id: ClientTableColumns.updatedAt,
      label: CLIENT_TABLE_COLUMN_LABELS[ClientTableColumns.updatedAt],
      fieldPath: "updatedAt",
      sortField: "updated_at",
      type: "date",
      defaultVisible: false,
      render: (value: unknown) => {
        return renderUpdatedAt(value);
      },
    },
    [ClientTableColumns.description]: {
      id: ClientTableColumns.description,
      label: CLIENT_TABLE_COLUMN_LABELS[ClientTableColumns.description],
      fieldPath: "description",
      sortField: "description",
      type: "custom",
      defaultVisible: false,
      render: (value: unknown) => {
        return renderDescription(value);
      },
    },
  },
  defaultColumnOrder: DEFAULT_CLIENT_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_CLIENT_TABLE_COLUMN_SETTINGS,
});

/**
 * Helper function to get default filter values
 */
export const getDefaultFilterValues = () => ({
  ...DEFAULT_CLIENT_FILTER_VALUES,
});

/**
 * Helper function to check if filters are active
 */
export const areFiltersActive = (filterValues: {
  nameSearch: string;
  domainSearch: string;
  emailSearch: string;
}): boolean => {
  return Boolean(
    (filterValues.nameSearch && filterValues.nameSearch.trim()) ||
      (filterValues.domainSearch && filterValues.domainSearch.trim()) ||
      (filterValues.emailSearch && filterValues.emailSearch.trim()),
  );
};

/**
 * Custom render functions for specific columns
 */
export function renderId(value: unknown): React.ReactNode {
  return <Text truncate>{String(value)}</Text>;
}

export function renderName(
  value: unknown,
  row: ClientFragment,
  onDetailsClick: (clientId: string) => void,
): React.ReactNode {
  return (
    <Clickable onClick={() => onDetailsClick(row.id)} outline="sm" stretch>
      <Table.CompanyCell name={String(value)} domain={row.domain} />
    </Clickable>
  );
}

export function renderDomain(value: unknown): React.ReactNode {
  return <Text>{String(value)}</Text>;
}

export function renderEmail(value: unknown): React.ReactNode {
  return <Text>{String(value)}</Text>;
}

export function renderCreatedAt(value: unknown): React.ReactNode {
  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderUpdatedAt(value: unknown): React.ReactNode {
  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderDescription(value: unknown): React.ReactNode {
  return <Text>{String(value)}</Text>;
}
